import React, { useState } from 'react';
import { colors } from '../../styles/tokens';
import { fmtInt, fmtMoney, fmtPct, fmtVenta, mmss } from '../../utils/format';
import { GLOSARIO, semaforo } from '../../utils/glosario';
import { useTableSort } from '../../utils/sort';
import type { SucursalTableRow } from '../../utils/tabla';
import { useEsMovil } from '../../hooks/useEsMovil';
import ModalEnfoque from '../ModalEnfoque';
import ExportButton from '../ExportButton';
import { exportXlsx, buildSucursalesSheet } from '../../utils/exportXlsx';

export interface TablaSucursalesProps {
  /** Filas YA unidas + ordenadas desc por consultas + recortadas al top 40 (`buildSucursalTableRows`). */
  rows: SucursalTableRow[];
  /**
   * TODAS las filas del periodo/filtro, sin recortar al top N (ítem 5,
   * 2026-07-28): alimenta el export a Excel y el modo enfoque. Si no se
   * pasa, se cae a `rows` (el top N) — así los consumidores viejos y las
   * pruebas que sólo pasan `rows` siguen funcionando.
   */
  rowsAll?: SucursalTableRow[];
  /** Total de sucursales unidas ANTES de recortar (para la nota "Mostrando N de M"). */
  total: number;
  /** Clic en una fila -> abre la Vista Tienda de esa sucursal (`Store.id`). */
  onOpenStore: (storeId: string) => void;
  /**
   * `true` cuando TODAS las filas del periodo traen `paresVendidos` null
   * (Proscai caído/frío, HF Fix 3): la columna "Venta pares" muestra
   * "calculando…"/"N/D" en vez del número (ver `ventaCalculando`). Default
   * `false` (no rompe a los consumidores/pruebas que no les importa este caso).
   */
  ventaUnavailable?: boolean;
  /** `true` mientras sigue pendiente el reintento automático de Venta (HF Fix 3). Default `false`. */
  ventaCalculando?: boolean;
}

const thBase: React.CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '.05em',
  textTransform: 'uppercase',
  color: colors.texto.quaternary,
  fontWeight: 700,
};

const thLeft: React.CSSProperties = { ...thBase, textAlign: 'left', padding: '11px 16px' };
const thRight: React.CSSProperties = { ...thBase, textAlign: 'right', padding: '11px 12px' };
const thRightLast: React.CSSProperties = { ...thBase, textAlign: 'right', padding: '11px 16px' };

/** Columna ordenable del encabezado (clic -> `toggle(key)` del `useTableSort`). */
interface ColumnaSucursal {
  key: keyof SucursalTableRow;
  label: string;
  style: React.CSSProperties;
  title?: string;
  testId: string;
}

/**
 * Encabezados con los rótulos del GLOSARIO (12-oct). Había DOS columnas
 * «ATENDIDAS» —el conteo y el % A ÷ S— y la del % se llamaba «Atendidas %» en
 * el teléfono: ahora el % es «% atendidas» en los dos.
 */
const COLUMNAS: ColumnaSucursal[] = [
  { key: 'name', label: 'Sucursal', style: thLeft, testId: 'th-name' },
  { key: 'consultas', label: GLOSARIO.consultas.rotulo, style: thRight, testId: 'th-consultas' },
  { key: 'solicitudes', label: GLOSARIO.solicitudes.rotulo, style: thRight, testId: 'th-solicitudes' },
  { key: 'atendidas', label: GLOSARIO.atendidas.rotulo, style: thRight, testId: 'th-atendidas' },
  { key: 'negados', label: GLOSARIO.negados.rotulo, style: thRight, testId: 'th-negados' },
  { key: 'venta', label: GLOSARIO.venta.rotulo, style: thRight, testId: 'th-venta' },
  { key: 'contadoMonto', label: 'Contado $', style: thRight, testId: 'th-contado' },
  { key: 'creditoMonto', label: 'Crédito $', style: thRight, testId: 'th-credito' },
  {
    key: 'tasa',
    label: GLOSARIO.tasa.rotulo,
    style: thRight,
    title: `${GLOSARIO.tasa.formula} — ${GLOSARIO.tasa.nota}`,
    testId: 'th-tasa',
  },
  {
    key: 'efect',
    label: GLOSARIO.efectividad.rotulo,
    style: thRight,
    title: `${GLOSARIO.efectividad.formula} — ${GLOSARIO.efectividad.nota}`,
    testId: 'th-efect',
  },
  {
    key: 'sla',
    label: GLOSARIO.pctAtendidas.rotulo,
    style: thRight,
    title: `${GLOSARIO.pctAtendidas.formula} — ${GLOSARIO.pctAtendidas.nota}`,
    testId: 'th-sla',
  },
  { key: 'tiempo', label: 'Tiempo', style: thRightLast, testId: 'th-tiempo' },
];

/* ------------------------------------------------------------------ *
 * MÓVIL: una TARJETA por sucursal, no una tabla de 12 columnas.
 *
 * La tabla mide ~1.038 px y la pantalla del teléfono 390: dejarla con
 * scroll lateral esconde justo las cifras que se miran —todo lo que no es
 * el nombre— detrás de un gesto que nadie descubre. Así que en móvil cada
 * fila se pinta como una tarjeta con pares etiqueta/valor.
 * ------------------------------------------------------------------ */

/** Cifra de la tarjeta de móvil: la misma celda del escritorio, con su etiqueta y su color semántico. */
interface MetricaMovil {
  label: string;
  valor: string;
  color: string;
}

/**
 * Las cifras que SÍ entran en la tarjeta de móvil, en orden de importancia:
 * el embudo operativo completo (consultas -> solicitudes -> atendidas /
 * negados), los pares vendidos, el MONTO vendido y la tasa de atención.
 *
 * El monto (contado + crédito) entró el 12-oct: sin él no había forma de ver en
 * un teléfono cuánto vendió una tienda. Fuera quedan Contado $ y Crédito $ por
 * separado, Efectividad, % atendidas y Tiempo: siguen en el export a Excel, en
 * el escritorio y —si se ordena por una de ellas— en la propia tarjeta (ver
 * `metricasMovil`).
 */
const METRICAS_MOVIL: Array<keyof SucursalTableRow> = [
  'consultas',
  'solicitudes',
  'atendidas',
  'negados',
  'venta',
  'montoTotal',
  'tasa',
];

/**
 * Traduce UNA columna del escritorio a su par etiqueta/valor de la tarjeta.
 * Usa exactamente los mismos formateadores y los mismos colores que la celda
 * de la tabla: esto es disposición, aquí no se recalcula nada.
 *
 * `name` devuelve `null` porque el nombre no es una cifra — encabeza la tarjeta.
 */
function metricaMovil(
  key: keyof SucursalTableRow,
  row: SucursalTableRow,
  ventaUnavailable: boolean,
  ventaCalculando: boolean,
): MetricaMovil | null {
  // Etiquetas del glosario, abreviadas sólo donde una celda de un tercio de
  // pantalla no da para más («Solicitudes a bodega» -> «Solicitudes»).
  switch (key) {
    case 'consultas':
      return { label: GLOSARIO.consultas.rotulo, valor: fmtInt(row.consultas), color: colors.naranja };
    case 'solicitudes':
      return { label: 'Solicitudes', valor: fmtInt(row.solicitudes), color: colors.brand };
    case 'atendidas':
      return { label: 'Atendidas', valor: fmtInt(row.atendidas), color: colors.verde };
    case 'negados':
      return { label: GLOSARIO.negados.rotulo, valor: fmtInt(row.negados), color: colors.rojo };
    case 'venta':
      return {
        label: GLOSARIO.venta.rotulo,
        valor: fmtVenta(row.venta, ventaUnavailable, ventaCalculando),
        color: colors.texto.body,
      };
    case 'montoTotal':
      return {
        label: 'Venta $',
        valor: ventaUnavailable ? (ventaCalculando ? 'calculando…' : 'N/D') : fmtMoney(row.montoTotal),
        color: colors.azulClaro,
      };
    case 'contadoMonto':
      return { label: 'Contado $', valor: fmtMoney(row.contadoMonto), color: colors.serie.contado };
    case 'creditoMonto':
      return { label: 'Crédito $', valor: fmtMoney(row.creditoMonto), color: colors.serie.credito };
    case 'tasa':
      return {
        label: GLOSARIO.tasa.rotulo,
        valor: fmtPct(row.tasa),
        color: semaforo('tasa', row.tasa),
      };
    case 'efect':
      return { label: 'Efectividad', valor: fmtPct(row.efect), color: colors.texto.body };
    case 'sla':
      return {
        label: GLOSARIO.pctAtendidas.rotulo,
        valor: fmtPct(row.sla),
        color: semaforo('atendidasBodega', row.sla),
      };
    case 'tiempo':
      return { label: 'Tiempo', valor: mmss(row.tiempo), color: colors.texto.secondary };
    default:
      return null;
  }
}

/**
 * Las cifras de la tarjeta: las 7 fijas y, además, la columna por la que el
 * usuario está ordenando cuando no es una de ellas. Ordenar por algo que no se
 * ve deja una lista barajada sin explicación.
 */
function metricasMovil(
  row: SucursalTableRow,
  sortKey: keyof SucursalTableRow,
  ventaUnavailable: boolean,
  ventaCalculando: boolean,
): MetricaMovil[] {
  const keys = [...METRICAS_MOVIL];
  if (sortKey !== 'name' && !keys.includes(sortKey)) {
    keys.push(sortKey);
  }
  return keys
    .map((key) => metricaMovil(key, row, ventaUnavailable, ventaCalculando))
    .filter((m): m is MetricaMovil => m !== null);
}

/**
 * Chip del selector de orden en móvil: es el MISMO control que el encabezado
 * clicable del escritorio (mismo `toggle`, mismo `data-testid`, mismo texto con
 * ▲/▼), sólo que un encabezado de tabla no existe cuando no hay tabla. 44 px de
 * alto para el dedo.
 */
const chipOrden: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 44,
  padding: '0 14px',
  borderRadius: 999,
  border: `1px solid ${colors.border}`,
  background: colors.card,
  color: colors.texto.secondary,
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  flex: 'none',
};

const chipOrdenActivo: React.CSSProperties = {
  ...chipOrden,
  background: colors.brandSoftBg,
  border: `1px solid ${colors.brandSoftBorder}`,
  color: colors.brand,
};

/**
 * Tabla "Detalle operativo por sucursal" de la vista Global: recreada
 * pixel-exact de `Tablero Colectivo Moda.dc.html` (líneas 85-120). Header
 * azul marca, filas clicables (abren la Vista Tienda de esa sucursal) con
 * hover manual vía estado (mismo patrón que `Combo`, ya que el hover
 * `tr[data-row]:hover` del handoff es una regla CSS global que este
 * componente no puede declarar por fila con estilos inline), y nota de pie
 * "Mostrando las 40 tiendas... de N" cuando el total sin recortar supera lo
 * mostrado (línea 119/394 del handoff).
 *
 * En teléfono (`useEsMovil`) la tabla se sustituye por una tarjeta por
 * sucursal — ver el bloque MÓVIL de arriba. El escritorio no cambia.
 */
const TablaSucursales: React.FC<TablaSucursalesProps> = ({
  rows,
  rowsAll,
  total,
  onOpenStore,
  ventaUnavailable = false,
  ventaCalculando = false,
}) => {
  const esMovil = useEsMovil();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  // 2026-08-24: se retiró "Ver todas". Hacía lo mismo que el modo enfoque pero
  // dentro de la página, empujando todo lo de abajo — que es justo lo que hacía
  // perder el hilo. Ahora hay UNA forma de ver el detalle completo: el enfoque,
  // que además muestra TODAS las filas, no sólo quita el tope de alto.
  // Modo enfoque (equivalente al "Expandir" de Power BI). Ver `ModalEnfoque`.
  const [enfocado, setEnfocado] = useState(false);
  const todas = rowsAll ?? rows;
  const visibles = enfocado ? todas : rows;
  // Default = el orden actual (`rows` ya llega desc por `consultas` desde
  // `buildSucursalTableRows`): ordenar de nuevo por la misma columna/dirección
  // reproduce el orden recibido (sort estable). El clic en un encabezado
  // reordena SOLO estas filas ya cargadas (top 40 acotado por el backend).
  const { sorted: sortedRows, sort, toggle } = useTableSort(visibles, 'consultas', 'desc');
  const showFooterNote = total > visibles.length;

  const botonExport = (
    <ExportButton
      testid="sucursales"
      variant="dark"
      disabled={todas.length === 0}
      onExport={() => exportXlsx([buildSucursalesSheet(todas)], 'sucursales.xlsx')}
    />
  );

  const botonCabecera: React.CSSProperties = {
    background: 'rgba(255,255,255,.14)',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    fontSize: 13,
    lineHeight: 1,
    fontWeight: 700,
    padding: '7px 10px',
    cursor: 'pointer',
    // Altura tocable en teléfono; en escritorio se queda en sus 27 px de siempre.
    ...(esMovil ? { minHeight: 44, minWidth: 44, fontSize: 16 } : null),
  };

  const contenido = (
    <div
      data-testid="tabla-sucursales"
      style={{
        marginTop: enfocado ? 0 : esMovil ? 12 : 16,
        // En enfoque la tarjeta ocupa todo el modal y la tabla se lleva el alto.
        ...(enfocado ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
                         border: 'none', borderRadius: 0, boxShadow: 'none' } : {}),
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(20,28,54,.05),0 18px 40px -30px rgba(20,28,54,.55)',
      }}
    >
      {!enfocado && (
      <div
        style={{
          background: colors.brand,
          color: '#fff',
          padding: esMovil ? '12px 14px' : '12px 20px',
          display: 'flex',
          // En móvil el título y la barra de acciones no caben en una fila de
          // 390 px: los botones se comprimían hasta quedar inservibles.
          flexDirection: esMovil ? 'column' : 'row',
          alignItems: esMovil ? 'stretch' : 'center',
          gap: esMovil ? 10 : 0,
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontWeight: 800, fontSize: 14, letterSpacing: '.02em' }}>
          Detalle operativo por sucursal
        </span>
        <div
          style={{
            display: 'flex',
            // `stretch` no es cosmético: `ExportButton` no fija altura propia,
            // así que estirándolo llega a los 44 px tocables sin tener que
            // modificar ese componente, que comparten otras cuatro vistas.
            alignItems: esMovil ? 'stretch' : 'center',
            gap: 10,
            minHeight: esMovil ? 44 : undefined,
          }}
        >
          <span
            style={{
              fontSize: 11.5,
              fontWeight: 500,
              opacity: 0.82,
              // Sólo en móvil: `minWidth: 0` deja que el texto se encoja/parta en
              // vez de empujar los botones fuera de la pantalla. En escritorio se
              // omite para que el header quede byte a byte como estaba.
              flex: esMovil ? 1 : undefined,
              minWidth: esMovil ? 0 : undefined,
              alignSelf: esMovil ? 'center' : undefined,
            }}
          >
            {esMovil
              ? 'Toca una tienda para filtrar'
              : 'Clic en una fila para filtrar el tablero por esa tienda'}
          </span>
          {botonExport}
          {!enfocado && (
            <button
              type="button"
              data-testid="tabla-sucursales-enfocar"
              onClick={() => setEnfocado(true)}
              aria-label="Expandir a pantalla completa"
              title="Expandir"
              style={botonCabecera}
            >
              ⤢
            </button>
          )}
        </div>
      </div>
      )}

      {esMovil ? (
        <>
          {/*
            Selector de orden: los mismos `data-testid` y el mismo `toggle` que
            los encabezados de la tabla, porque en móvil no hay encabezado que
            pulsar. La tira se desplaza en horizontal DENTRO de su caja —doce
            opciones apiladas se comerían media pantalla—: es una barra de
            controles conocidos, no datos escondidos, y la página sigue sin
            arrastrarse de lado.
          */}
          <div
            style={{
              padding: '10px 12px 0',
              borderBottom: `1px solid ${colors.rowBorder}`,
              minWidth: 0,
            }}
          >
            <span
              style={{
                display: 'block',
                fontSize: 11,
                letterSpacing: '.05em',
                textTransform: 'uppercase',
                color: colors.texto.quaternary,
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              Ordenar por
            </span>
            <div
              style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 10, minWidth: 0 }}
            >
              {COLUMNAS.map((col) => {
                const activo = sort.key === col.key;
                return (
                  <button
                    key={col.key}
                    type="button"
                    data-testid={col.testId}
                    tabIndex={0}
                    title={col.title}
                    onClick={() => toggle(col.key)}
                    style={activo ? chipOrdenActivo : chipOrden}
                  >
                    {col.label}
                    {activo && (sort.dir === 'asc' ? ' ▲' : ' ▼')}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            style={{
              // ⚠️ SIN TOPE DE ALTO (12-oct). Había una caja de 460 px con su
              // propio scroll: scroll dentro de scroll, y sólo cabían dos
              // tarjetas y media antes de la nota de pie. En el teléfono la
              // lista fluye con la página; en enfoque se lleva todo el alto.
              flex: enfocado ? 1 : undefined,
              minHeight: 0,
              overflowY: enfocado ? 'auto' : undefined,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: 12,
              background: colors.panelBg,
            }}
          >
            {sortedRows.map((row) => (
              <div
                key={row.storeId}
                data-testid={`fila-${row.storeId}`}
                role="button"
                tabIndex={0}
                onClick={() => onOpenStore(row.storeId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenStore(row.storeId);
                  }
                }}
                style={{
                  background: colors.card,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 14,
                  padding: 14,
                  cursor: 'pointer',
                  minWidth: 0,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  {/* `minWidth: 0` o el flex se niega a encoger el nombre y la tarjeta se sale de la pantalla. */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 14.5,
                        color: colors.texto.strong,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {row.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: colors.texto.muted,
                        fontWeight: 600,
                        marginTop: 2,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {row.sub}
                    </div>
                  </div>
                  <span
                    aria-hidden
                    style={{ fontSize: 20, lineHeight: 1, color: colors.chevron, flex: 'none' }}
                  >
                    ›
                  </span>
                </div>

                <div
                  style={{
                    display: 'grid',
                    // `minmax(0, 1fr)`: sin ese 0 las columnas se niegan a bajar
                    // del ancho de su contenido y la rejilla desborda la tarjeta.
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: '10px 8px',
                    marginTop: 12,
                  }}
                >
                  {metricasMovil(row, sort.key, ventaUnavailable, ventaCalculando).map((m) => (
                    <div key={m.label} style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: '.03em',
                          color: colors.texto.quaternary,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {m.label}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          marginTop: 1,
                          color: m.color,
                          fontVariantNumeric: 'tabular-nums',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {m.valor}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
      <div style={{ maxHeight: enfocado ? undefined : 320, flex: enfocado ? 1 : undefined, minHeight: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
          <thead>
            <tr
              style={{
                position: 'sticky',
                top: 0,
                background: '#fff',
                zIndex: 1,
                boxShadow: 'inset 0 -1px 0 #e7e9ef',
              }}
            >
              {COLUMNAS.map((col) => (
                <th
                  key={col.key}
                  data-testid={col.testId}
                  role="button"
                  tabIndex={0}
                  title={col.title}
                  onClick={() => toggle(col.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(col.key);
                    }
                  }}
                  style={{ ...col.style, cursor: 'pointer', userSelect: 'none' }}
                >
                  {col.label}
                  {sort.key === col.key && (sort.dir === 'asc' ? ' ▲' : ' ▼')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const hovered = hoveredId === row.storeId;

              return (
                <tr
                  key={row.storeId}
                  data-testid={`fila-${row.storeId}`}
                  onClick={() => onOpenStore(row.storeId)}
                  onMouseEnter={() => setHoveredId(row.storeId)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    cursor: 'pointer',
                    borderBottom: `1px solid ${colors.rowBorder}`,
                    background: hovered ? colors.rowHover : 'transparent',
                  }}
                >
                  <td style={{ padding: '9px 16px' }}>
                    <div style={{ fontWeight: 700, color: colors.texto.strong }}>{row.name}</div>
                    <div style={{ fontSize: 10.5, color: colors.texto.muted, fontWeight: 600, marginTop: 1 }}>
                      {row.sub}
                    </div>
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: colors.naranja, fontWeight: 600 }}>
                    {fmtInt(row.consultas)}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: colors.brand, fontWeight: 600 }}>
                    {fmtInt(row.solicitudes)}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: colors.verde, fontWeight: 600 }}>
                    {fmtInt(row.atendidas)}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: colors.rojo, fontWeight: 600 }}>
                    {fmtInt(row.negados)}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: colors.texto.body, fontWeight: 600 }}>
                    {fmtVenta(row.venta, ventaUnavailable, ventaCalculando)}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: colors.serie.contado, fontWeight: 600 }}>
                    {fmtMoney(row.contadoMonto)}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: colors.serie.credito, fontWeight: 600 }}>
                    {fmtMoney(row.creditoMonto)}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700, color: semaforo('tasa', row.tasa) }}>
                    {fmtPct(row.tasa)}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: colors.texto.body }}>
                    {fmtPct(row.efect)}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, color: semaforo('atendidasBodega', row.sla) }}>
                    {fmtPct(row.sla)}
                  </td>
                  <td style={{ padding: '9px 16px', textAlign: 'right', color: colors.texto.secondary, fontWeight: 600 }}>
                    {mmss(row.tiempo)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {showFooterNote && (
        <div
          style={{
            padding: esMovil ? '9px 14px' : '9px 18px',
            fontSize: 11.5,
            color: colors.texto.quaternary,
            fontWeight: 600,
            borderTop: `1px solid ${colors.rowBorder}`,
            background: colors.footerBg,
          }}
        >
          Mostrando las {visibles.length} tiendas con más consultas de {total} — usa el botón de
          expandir ⤢, el export a Excel, o los filtros de regional/supervisor/cadena para el
          detalle completo
        </div>
      )}
    </div>
  );

  if (!enfocado) return contenido;

  return (
    <ModalEnfoque
      testid="sucursales"
      titulo="Detalle operativo por sucursal"
      subtitulo={`${total} tienda${total === 1 ? '' : 's'} en el periodo`}
      acciones={botonExport}
      onClose={() => setEnfocado(false)}
    >
      {contenido}
    </ModalEnfoque>
  );
};

export default TablaSucursales;
